import React, { useEffect } from 'react';
import {
    SegmentedControl,
    Card,
    Text,
    Group,
    NumberInput,
    Grid,
    Avatar,
    Progress,
    Tooltip,
    Checkbox,
} from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { GroupMember } from '../../types/group';
import checkIcon from '../../assets/icons/check.svg';

interface RuleSplitControlsProps {
    form: UseFormReturnType<any>;
    members: GroupMember[];
    amount: number;
}

export const RuleSplitControls: React.FC<RuleSplitControlsProps> = ({
    form,
    members,
    amount,
}) => {
    // Make sure amount is a valid number
    const validAmount = isNaN(amount) ? 0 : amount;

    // Update split details when type changes
    useEffect(() => {
        if (form.values.split_type) {
            updateSplitDetails(form.values.split_type);
        }
    }, [validAmount, form.values.split_type]);

    // Calculate splits based on split type
    const updateSplitDetails = (type: string) => {
        const memberDetails: Record<string, number> = {};
        const availableMembers = members.filter(
            m => m.status === 'active' || m.status === 'ghost' || m.status === 'pending'
        );

        switch (type) {
            case 'equal':
                const perMember = availableMembers.length > 0
                    ? parseFloat((validAmount / availableMembers.length).toFixed(2))
                    : 0;

                availableMembers.forEach((member, index) => {
                    // Handle rounding issues - add remainder to first member
                    let memberAmount = perMember;
                    if (index === 0) {
                        const expectedTotal = parseFloat((perMember * availableMembers.length).toFixed(2));
                        const remainder = parseFloat((validAmount - expectedTotal).toFixed(2));
                        memberAmount = parseFloat((memberAmount + remainder).toFixed(2));
                    }
                    memberDetails[member.id] = memberAmount;
                });
                break;

            case 'percentage':
                const defaultPercent = availableMembers.length > 0
                    ? parseFloat((100 / availableMembers.length).toFixed(2))
                    : 0;

                availableMembers.forEach(member => {
                    memberDetails[member.id] = defaultPercent;
                });
                break;

            case 'shares':
                availableMembers.forEach(member => {
                    memberDetails[member.id] = 1; // one share each by default
                });
                break;

            case 'exact':
                availableMembers.forEach(member => {
                    memberDetails[member.id] = 0; // zero amount each by default
                });
                break;
        }

        form.setFieldValue('split_type', type as any);
        form.setFieldValue('split_with', memberDetails);
        console.log(`[RuleSplitControls updateSplitDetails] Updated split_type to: ${type}, split_with:`, memberDetails);
    };

    // Render split input based on split type
    const renderSplitDetails = () => {
        const { split_type, split_with } = form.values;

        if (!split_with || Object.keys(split_with).length === 0) {
            return null;
        }

        let splitSum = 0;

        // Calculate the current total for percentage and exact splits
        if (split_type === 'percentage') {
            splitSum = Object.values(split_with).reduce((sum: number, value: any) => sum + Number(value || 0), 0);
        } else if (split_type === 'exact') {
            splitSum = Object.values(split_with).reduce((sum: number, value: any) => sum + Number(value || 0), 0);
        } else if (split_type === 'shares') {
            const totalShares = Object.values(split_with).reduce((sum: number, value: any) => sum + Number(value || 0), 0);
            splitSum = totalShares > 0 ? parseFloat((validAmount * (totalShares / totalShares)).toFixed(2)) : 0;
        } else {
            splitSum = parseFloat((Object.values(split_with).reduce((sum: number, value: any) => sum + Number(value || 0), 0)).toFixed(2));
        }

        // Calculate progress percentage for the bar
        const amountProgress = split_type === 'percentage'
            ? (splitSum / 100) * 100 // Percentage of 100%
            : (split_type === 'exact' ? (validAmount > 0 ? (splitSum / validAmount) * 100 : 0) : 100);

        return (
            <Card withBorder shadow="sm" padding="md" radius="md" mt="md">
                <Text fw={600} size="sm">Split Details</Text>

                {/* Progress bar for exact and percentage splits */}
                {(split_type === 'exact' || split_type === 'percentage') && (
                    <Group mt="xs">
                        <Progress
                            value={Math.min(amountProgress, 100)}
                            color={amountProgress > 100 ? 'red' : amountProgress === 100 ? 'green' : 'blue'}
                            size="sm"
                            radius="xl"
                            style={{ width: '100%' }}
                        />
                        <Group gap="xs">
                            <Text size="xs" c={amountProgress === 100 ? 'green' : (amountProgress > 100 ? 'red' : 'dimmed')}>
                                {split_type === 'percentage' ? `${splitSum.toFixed(1)}%` : `$${splitSum.toFixed(2)}`}
                            </Text>
                            <Text size="xs" c="dimmed">/</Text>
                            <Text size="xs" c="dimmed">
                                {split_type === 'percentage' ? '100%' : `$${validAmount.toFixed(2)}`}
                            </Text>
                            {amountProgress === 100 && <img src={checkIcon} alt="Check" width={16} />}
                        </Group>
                    </Group>
                )}

                <Grid mt="sm">
                    {Object.keys(split_with).map(memberId => {
                        const member = members.find(m => m.id.toString() === memberId);
                        if (!member) return null;

                        let input;
                        switch (split_type) {
                            case 'exact':
                                input = (
                                    <Group justify="space-between" style={{ width: '100%' }}>
                                        <Group>
                                            <Avatar
                                                src={member.avatar_url}
                                                radius="xl"
                                                size="sm"
                                            >
                                                {member.display_name ? member.display_name.charAt(0) : member.email.charAt(0)}
                                            </Avatar>
                                            <Text size="sm">{member.display_name || member.email}</Text>
                                        </Group>
                                        <NumberInput
                                            value={Number(split_with[memberId] || 0)}
                                            onChange={(value) => {
                                                const newSplitWith = { ...split_with };
                                                newSplitWith[memberId] = Number(value) || 0;
                                                form.setFieldValue('split_with', newSplitWith);
                                                console.log('[RuleSplitControls onChange Exact] Set split_with:', newSplitWith);
                                            }}
                                            min={0}
                                            step={0.01}
                                            style={{ width: '120px' }}
                                            rightSection={
                                                <Tooltip label="Set to 0 to exclude this member from the split">
                                                    <Text size="xs" style={{ cursor: 'help' }}>$</Text>
                                                </Tooltip>
                                            }
                                        />
                                    </Group>
                                );
                                break;

                            case 'percentage':
                                // Calculate the actual amount based on the percentage
                                const percentAmount = validAmount * (Number(split_with[memberId] || 0) / 100);

                                input = (
                                    <Group justify="space-between" style={{ width: '100%' }}>
                                        <Group>
                                            <Avatar
                                                src={member.avatar_url}
                                                radius="xl"
                                                size="sm"
                                            >
                                                {member.display_name ? member.display_name.charAt(0) : member.email.charAt(0)}
                                            </Avatar>
                                            <Text size="sm">{member.display_name || member.email}</Text>
                                        </Group>
                                        <Group>
                                            <NumberInput
                                                value={Number(split_with[memberId] || 0)}
                                                onChange={(value) => {
                                                    const newSplitWith = { ...split_with };
                                                    newSplitWith[memberId] = Number(value) || 0;
                                                    form.setFieldValue('split_with', newSplitWith);
                                                    console.log('[RuleSplitControls onChange Percentage] Set split_with:', newSplitWith);
                                                }}
                                                min={0}
                                                max={100}
                                                step={1}
                                                style={{ width: '90px' }}
                                                rightSection={<Text size="xs">%</Text>}
                                            />
                                            <Text size="xs" c="dimmed">
                                                ${isNaN(percentAmount) ? '0.00' : percentAmount.toFixed(2)}
                                            </Text>
                                        </Group>
                                    </Group>
                                );
                                break;

                            case 'shares':
                                const totalShares = Object.values(split_with).reduce((sum: number, value: any) => sum + Number(value || 0), 0);
                                const memberShares = Number(split_with[memberId] || 0);
                                const shareAmount = totalShares > 0 && memberShares > 0
                                    ? (validAmount * (memberShares / totalShares))
                                    : 0;

                                input = (
                                    <Group justify="space-between" style={{ width: '100%' }}>
                                        <Group>
                                            <Avatar
                                                src={member.avatar_url}
                                                radius="xl"
                                                size="sm"
                                            >
                                                {member.display_name ? member.display_name.charAt(0) : member.email.charAt(0)}
                                            </Avatar>
                                            <Text size="sm">{member.display_name || member.email}</Text>
                                        </Group>
                                        <Group>
                                            <NumberInput
                                                value={memberShares}
                                                onChange={(value) => {
                                                    const newSplitWith = { ...split_with };
                                                    newSplitWith[memberId] = Number(value) || 0;
                                                    form.setFieldValue('split_with', newSplitWith);
                                                    console.log('[RuleSplitControls onChange Shares] Set split_with:', newSplitWith);
                                                }}
                                                min={0}
                                                step={1}
                                                style={{ width: '90px' }}
                                            />
                                            <Text size="xs" c="dimmed">
                                                ${isNaN(shareAmount) ? '0.00' : shareAmount.toFixed(2)}
                                            </Text>
                                        </Group>
                                    </Group>
                                );
                                break;

                            default: // equal
                                const splitAmount = Number(split_with[memberId] || 0);
                                input = (
                                    <Group justify="space-between" style={{ width: '100%' }}>
                                        <Group>
                                            <Checkbox
                                                checked={splitAmount > 0}
                                                onChange={(e) => {
                                                    const newSplitDetails = { ...split_with };
                                                    if (e.currentTarget.checked) {
                                                        // Add member to split
                                                        const activeMemberCount = Object.entries(newSplitDetails)
                                                            .filter(([_, value]) => Number(value) > 0).length + 1;
                                                        const perMember = parseFloat((validAmount / activeMemberCount).toFixed(2));

                                                        // Handle rounding issues by distributing the remainder
                                                        let appliedTotal = 0;

                                                        Object.keys(newSplitDetails).forEach(id => {
                                                            if (Number(newSplitDetails[id]) > 0 || id === memberId) {
                                                                newSplitDetails[id] = perMember;
                                                                appliedTotal += perMember;
                                                            }
                                                        });

                                                        // Add any remainder to the first active member
                                                        const remainder = parseFloat((validAmount - appliedTotal).toFixed(2));
                                                        if (remainder !== 0) {
                                                            for (const id of Object.keys(newSplitDetails)) {
                                                                if (Number(newSplitDetails[id]) > 0) {
                                                                    newSplitDetails[id] = parseFloat((Number(newSplitDetails[id]) + remainder).toFixed(2));
                                                                    break;
                                                                }
                                                            }
                                                        }
                                                    } else {
                                                        // Remove member from split
                                                        newSplitDetails[memberId] = 0;
                                                        const remainingMembers = Object.keys(newSplitDetails)
                                                            .filter(id => Number(newSplitDetails[id]) > 0);

                                                        if (remainingMembers.length > 0) {
                                                            const perMember = parseFloat((validAmount / remainingMembers.length).toFixed(2));

                                                            // Handle rounding issues by distributing the remainder
                                                            let appliedTotal = 0;

                                                            remainingMembers.forEach(id => {
                                                                newSplitDetails[id] = perMember;
                                                                appliedTotal += perMember;
                                                            });

                                                            // Add any remainder to the first active member
                                                            const remainder = parseFloat((validAmount - appliedTotal).toFixed(2));
                                                            if (remainder !== 0 && remainingMembers.length > 0) {
                                                                newSplitDetails[remainingMembers[0]] = parseFloat(
                                                                    (Number(newSplitDetails[remainingMembers[0]]) + remainder).toFixed(2)
                                                                );
                                                            }
                                                        }
                                                    }
                                                    form.setFieldValue('split_with', newSplitDetails);
                                                    console.log('[RuleSplitControls onChange Equal] Set split_with:', newSplitDetails);
                                                }}
                                            />
                                            <Avatar
                                                src={member.avatar_url}
                                                radius="xl"
                                                size="sm"
                                            >
                                                {member.display_name ? member.display_name.charAt(0) : member.email.charAt(0)}
                                            </Avatar>
                                            <Text size="sm">{member.display_name || member.email}</Text>
                                        </Group>
                                        <Text size="sm" fw={500}>
                                            ${isNaN(splitAmount) ? '0.00' : splitAmount.toFixed(2)}
                                        </Text>
                                    </Group>
                                );
                        }

                        return (
                            <Grid.Col key={memberId} span={12}>
                                {input}
                            </Grid.Col>
                        );
                    })}
                </Grid>
            </Card>
        );
    };

    return (
        <>
            <SegmentedControl
                value={form.values.split_type}
                onChange={(value) => updateSplitDetails(value)}
                data={[
                    { label: 'Equal', value: 'equal' },
                    { label: 'Exact', value: 'exact' },
                    { label: 'Percentage', value: 'percentage' },
                    { label: 'Shares', value: 'shares' },
                ]}
                fullWidth
                radius="md"
            />

            {renderSplitDetails()}
        </>
    );
}; 